import { useEffect } from 'react'
import dayjs from 'dayjs'
import duration from 'dayjs/plugin/duration'
import relativeTime from 'dayjs/plugin/relativeTime'
import Analytics from './components/analytics/Analytics'
import AppRoutes from './pages/routes'
import useTabTitle from './hooks/useTabTitle'
import { useMetaPixel } from './hooks/useMetaPixel'
import { ErrorBoundary } from 'components/ErrorBoundary'
import { SnackbarProvider } from './contexts/SnackbarContext'
import { SnackbarContainer } from './components/SnackbarContainer'
import { EmailProviderPickerModal } from './components/EmailProviderPickerModal'
import { BoxTruckBidNotifications } from 'features/box-truck-loads'
import { consumePendingRelocationNotice } from './setup/authTokenStorage'
import { showWarning } from './utils/snackbar'

// Initialize Day.js plugins
dayjs.extend(duration)
dayjs.extend(relativeTime)

declare global {
    interface Window {
        onRegularCall: () => void
    }
}

export default function App() {
    // Use the tab title hook to show the page name and notification count in browser tab.
    // Every page's name, the signing page's included, comes from `constants/routeTitles`.
    useTabTitle()

    // Initialize Meta Pixel (Facebook Pixel) for tracking
    useMetaPixel()

    // Flag set by SetupAxios's recoverFromUnauthorized right before the hard reload that follows a
    // switched tab's override 401ing back to the base company — a silent relocation with no other
    // user-facing cue. Checked here (App, not AuthInitializer) specifically because effects fire
    // children-before-parent on mount: SnackbarProvider is a descendant of App, so by the time
    // THIS effect runs, SnackbarProvider's own effect has already registered the snackbar instance.
    // Checking any earlier (e.g. inside AuthInitializer, which renders App as its children) would
    // race showWarning() against SnackbarProvider not having mounted yet and silently no-op.
    useEffect(() => {
        if (consumePendingRelocationNotice()) {
            showWarning('Your session for that company ended — you were returned to your default company.')
        }
    }, [])

    return (
        <ErrorBoundary>
            <SnackbarProvider>
                {/* Unified analytics tracking */}
                <Analytics projectName="numeo-freight" />

                {/* Snackbar container for rendering stacked snackbars */}
                <SnackbarContainer />
                {/* Persistent bottom-right stack of live driver-bid notifications (Box Truck operators only) */}
                <BoxTruckBidNotifications />
                <EmailProviderPickerModal />

                {/* Main application routes */}
                <AppRoutes />
            </SnackbarProvider>
        </ErrorBoundary>
    )
}
